/* eslint-disable max-lines */
import { useMemo, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import toast from 'react-hot-toast';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { Label, Radio, RadioGroup } from '@headlessui/react';

import Button from '@components/atoms/ui-kit/button.tsx';
import Input from '@components/atoms/ui-kit/input.tsx';
import Modal from '@components/atoms/modals/modal.tsx';
import Checkbox from '@components/atoms/ui-kit/checkbox.tsx';
import DataGridV2, {
  type DataGridColumn
} from '@components/atoms/datatable/data-grid-v2';

import { useGetResellerUserLogin } from '@utils/api/resellers/use-get-user-logins.tsx';
import {
  useCreateCustomer,
  type CustomerPayload
} from '@utils/api/resellers/use-create-user-login.tsx';
import { useDeleteResellerCustomerLogin } from '@utils/api/resellers/use-delete-customer-reseller.tsx';
import { useDeactivateResellerCustomerLogin } from '@utils/api/resellers/use-block-user-reseller.tsx';
import { useResetResellerUserPassword } from '@utils/api/resellers/use-reset-user-password.tsx';
import { useResendActivationLink } from '@utils/api/resellers/use-resend-activation-link-customer.tsx';
import { type UserLogin } from '@utils/types/resellers/user-login.ts';
import { AxiosError, isAxiosError } from 'axios';
import { BaseError } from '@utils/types/shared/base.ts';
import { Check, Plus, Send, Settings2, Trash2 } from 'lucide-react';
import type { GridAction } from '@components/atoms/datatable/grid-action-controller';
import ConfirmationModal from '@components/atoms/modals/confirmation-modal.tsx';

export const statusType = [
  { id: 1, title: 'Reset password' },
  { id: 2, title: 'Block user' }
];

interface UsersDetailsProps {
  resellerId?: string;
}

type Row = {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  is_active: boolean;
};

export const UsersDetails = ({ resellerId }: UsersDetailsProps) => {
  const { t } = useTranslation();
  const formRef = useRef<HTMLFormElement>(null);

  const [isDeleteUserModalOpen, setIsDeleteUserModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserLogin>();
  const [isEditModalOpen, setEditModalOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState(statusType[0]);
  const [addUserOpen, setAddUserOpen] = useState(false);
  const [resendLinkModal, setRsendLinkModal] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isResendingLink, setIsResendingLink] = useState(false);

  const { data, isLoading } = useGetResellerUserLogin(resellerId || '');
  const createCustomerMutation = useCreateCustomer(resellerId || '');
  const { mutateAsync: deleteResellerCustomerLoginMutation } =
    useDeleteResellerCustomerLogin(Number(resellerId));
  const { mutateAsync: deactivateUserLoginMutation } =
    useDeactivateResellerCustomerLogin(
      Number(resellerId),
      selectedUser?.id || 0
    );
  const resetPasswordMutation = useResetResellerUserPassword(resellerId || '');
  const resendActivationLink = useResendActivationLink();

  const schema = yup.object().shape({
    first_name: yup.string().required(t('first-name-is-required')),
    last_name: yup.string().required(t('last-name-is-required')),
    email: yup
      .string()
      .email(t('invalid-email'))
      .required(t('email-is-required')),
    roles: yup.array().min(1, t('role-required')).of(yup.string().required())
  });

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors }
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: { first_name: '', last_name: '', email: '', roles: [] }
  });

  const rows: Row[] = useMemo(() => {
    if (!data) return [];
    return data.result.map((u: UserLogin) => ({
      id: u.id,
      first_name: u.first_name,
      last_name: u.last_name,
      email: u.email.toLowerCase(),
      is_active: u.is_active
    }));
  }, [data]);

  const columns = useMemo<DataGridColumn<Row>[]>(
    () => [
      { id: 'id', header: t('id'), accessorKey: 'id', maxWidth: 90 },
      {
        id: 'first_name',
        header: t('first-name'),
        accessorKey: 'first_name',
        minWidth: 160
      },
      {
        id: 'last_name',
        header: t('last-name'),
        accessorKey: 'last_name',
        minWidth: 160
      },
      {
        id: 'email',
        header: t('email'),
        accessorKey: 'email',
        minWidth: 220,
        cell: ({ getValue }) => String(getValue() ?? '')
      },
      {
        id: 'is_active',
        header: t('status'),
        accessorKey: 'is_active',
        maxWidth: 140,
        cell: ({ getValue }) =>
          (getValue() as boolean) ? (
            <span className="text-green-500">{t('active')}</span>
          ) : (
            <span className="text-red-500">{t('inactive')}</span>
          )
      }
    ],
    [t]
  );

  const onSubmit = async (payload: CustomerPayload) => {
    setIsCreating(true);
    try {
      await createCustomerMutation.mutateAsync(payload);
      toast.success(t('customer-created-successfully'));
      setAddUserOpen(false);
      reset();
    } catch (error) {
      let message = t('an-error-occurred-while-creating-the-customer');
      if (isAxiosError<BaseError>(error)) {
        const err = error.response?.data;
        message =
          err?.error_description ||
          err?.detail ||
          err?.message ||
          err?.error ||
          message;
      }
      toast.error(message);
    } finally {
      setIsCreating(false);
    }
  };

  const handleDeleteUser = async (userId: number) => {
    setIsDeleting(true);
    await new Promise(r => setTimeout(r, 300));
    try {
      await deleteResellerCustomerLoginMutation(userId);
      toast.success(t('user-deleted-successfully'));
    } catch (error) {
      let message = t('error-deleting-user-please-try-again-later');
      if (isAxiosError<BaseError>(error)) {
        const err = error.response?.data;
        message =
          err?.error_description ||
          err?.detail ||
          err?.message ||
          err?.error ||
          message;
      }
      toast.error(message);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleUserAction = async (userId: number) => {
    try {
      if (selectedOption.id === 1) {
        await resetPasswordMutation.mutateAsync({ userId });
        toast.success(t('password-reset-successfully'));
      } else if (selectedOption.id === 2) {
        await deactivateUserLoginMutation();
        toast.success(t('user-deactivated-successfully'));
      }
    } catch (error) {
      const data = (error as AxiosError<BaseError>)?.response?.data;
      const messages = Array.isArray(data?.detail)
        ? // eslint-disable-next-line @typescript-eslint/no-explicit-any
          data.detail.map((e: any) => `${e.loc?.[1] ?? 'field'}: ${e.msg}`)
        : [
            data?.message ||
              data?.error_description ||
              data?.error ||
              t('error-performing-action-please-try-again-later')
          ];
      toast.error(messages.join('\n'));
    }
  };

  const handleResendActivation = async (): Promise<void> => {
    if (!resellerId || !selectedUser?.id) return;

    setIsResendingLink(true);
    try {
      await new Promise(r => setTimeout(r, 300));

      await resendActivationLink.mutateAsync({
        customerId: Number(resellerId),
        userId: Number(selectedUser.id)
      });

      toast.success(t('activation-link-resent-successfully'));
      setRsendLinkModal(false);
    } catch (error) {
      const err = error as AxiosError<BaseError>;
      const data = err?.response?.data;
      const fallback = t('failed-to-resent-activation-link');
      const messages = Array.isArray(data?.detail)
        ? // eslint-disable-next-line @typescript-eslint/no-explicit-any
          data.detail.map((e: any) => `${e.loc?.[1] ?? 'field'}: ${e.msg}`)
        : [data?.message || data?.error_description || fallback];
      toast.error(messages.join('\n'));
    } finally {
      setIsResendingLink(false);
    }
  };

  // Row actions replacing the old menuOptions
  const actions = useMemo<GridAction<Row>[]>(
    () => [
      {
        id: 'permissions',
        label: t('manage-user-permissions'),
        icon: <Settings2 className="h-4 w-4" />,
        intent: 'primary',
        run: row => {
          setSelectedUser(row as unknown as UserLogin);
          setEditModalOpen(true);
        }
      },
      {
        id: 'link',
        label: t('send-reactivation-link'),
        icon: <Send className="h-4 w-4" />,
        intent: 'neutral',
        run: row => {
          setSelectedUser(row as unknown as UserLogin);
          setRsendLinkModal(true);
        }
      },
      {
        id: 'delete',
        label: t('delete'),
        icon: <Trash2 className="h-4 w-4" />,
        intent: 'danger',
        run: row => {
          setSelectedUser(row as unknown as UserLogin);
          setIsDeleteUserModalOpen(true);
        }
      }
    ],
    [t]
  );

  const FORM_ID = 'add-user-form';

  return (
    <div className="p-4 space-y-2">
      <div className="flex justify-between items-center">
        <div />
        <Button
          onClick={() => setAddUserOpen(true)}
          icon={<Plus width={14} />}
          size="small"
          label={t('add-user')}
          variant="secondary"
          className="w-fit"
        />
      </div>

      {/* Add user modal */}
      <Modal
        isOpen={addUserOpen}
        onClose={() => setAddUserOpen(false)}
        size="small"
        title={t('add-user')}
        footerActions={[
          <Button
            key="cancel"
            onClick={() => setAddUserOpen(false)}
            label={t('cancel')}
            size="small"
            variant="smoke"
            disabled={isCreating}
          />,
          <Button
            label={isCreating ? t('adding-user') : t('add-user')}
            icon={<Plus width={18} />}
            onClick={() => formRef.current?.requestSubmit()}
            size="small"
            key="create"
            disabled={isCreating}
            variant="primary"
          />
        ]}
      >
        <form
          onSubmit={handleSubmit(onSubmit)}
          ref={formRef}
          key="create"
          id={FORM_ID}
        >
          <div className="space-y-4">
            <Controller
              name="first_name"
              control={control}
              render={({ field }) => (
                <Input
                  {...field}
                  label={t('first-name')}
                  id="firstName"
                  placeholder={t('first-name')}
                  error={errors.first_name?.message}
                />
              )}
            />
            <Controller
              name="last_name"
              control={control}
              render={({ field }) => (
                <Input
                  {...field}
                  label={t('last-name')}
                  id="last_name"
                  placeholder={t('last-name')}
                  error={errors.last_name?.message}
                />
              )}
            />
            <Controller
              name="email"
              control={control}
              render={({ field }) => (
                <Input
                  {...field}
                  label={t('email')}
                  id="email"
                  placeholder={t('email')}
                  type="email"
                  error={errors.email?.message}
                />
              )}
            />
            <div>
              <label
                htmlFor="roles"
                className="block text-sm font-medium text-gray-700 dark:text-zinc-300"
              >
                {t('role')}
              </label>
              <Controller
                name="roles"
                control={control}
                defaultValue={[]}
                render={({ field: { value, onChange } }) => {
                  const currentRoles = value ?? [];
                  const roles = ['admin', 'contributor'];
                  return (
                    <fieldset>
                      <div className="gap-1 grid">
                        {roles.map(role => (
                          <Checkbox
                            key={role}
                            id={`role-${role}`}
                            name="roles"
                            label={t(role)}
                            checked={currentRoles.includes(role)}
                            onChange={e => {
                              if (e.target.checked)
                                onChange([...currentRoles, role]);
                              else
                                onChange(
                                  currentRoles.filter((v: string) => v !== role)
                                );
                            }}
                            variant="primary"
                            wrapperClass="grid items-center"
                            labelClass="ml-3 text-sm font-medium leading-6"
                            className="h-3 w-3"
                          />
                        ))}
                      </div>
                    </fieldset>
                  );
                }}
              />
              {errors.roles && (
                <p className="text-red-500 text-xs">{errors.roles.message}</p>
              )}
            </div>
          </div>
        </form>
      </Modal>

      <ConfirmationModal
        isOpen={isDeleteUserModalOpen}
        onClose={() => setIsDeleteUserModalOpen(false)}
        onConfirm={async () => {
          if (!selectedUser?.id) return;
          await handleDeleteUser(selectedUser.id);
          setIsDeleteUserModalOpen(false);
        }}
        size="small"
        title={t('delete-confirmation')}
        caption={t('are-you-sure-you-want-to-delete-this-user')}
        confirmLabel={isDeleting ? t('deleting') : t('delete')}
        cancelLabel={t('cancel')}
        confirmDisabled={isDeleting}
        destructive
        autoFocusConfirm
      />
      {/* Table */}
      <div className="pt-2">
        <DataGridV2<Row>
          data={rows}
          columns={columns}
          actions={actions}
          enableRowSelection={false}
          enablePagination
          manualPagination
          enableSorting={false}
          manualSorting={false}
          enableFiltering={false}
          manualFiltering={false}
          totalItems={data?.total_results ?? 0}
          pageSize={25}
          pageSizeOptions={[25, 50, 75, 100]}
          showColumnFilters={false}
          filterMode="popover"
          height="55vh"
          dense
          grid={false}
          striped
          loading={isLoading || !data}
          getRowId={row => String(row.id)}
          onCellClick={(row, columnId) => {
            if (
              ['first_name', 'last_name', 'email'].includes(columnId as string)
            ) {
              setSelectedUser(row as unknown as UserLogin);
              setEditModalOpen(true);
            }
          }}
        />
      </div>

      <ConfirmationModal
        isOpen={resendLinkModal}
        onClose={() => setRsendLinkModal(false)}
        onConfirm={handleResendActivation}
        size="small"
        title={t('resend-activation-link')}
        caption={t(
          'by-clicking-confirm-you-will-resend-activation-link-to-the-users-email'
        )}
        confirmLabel={isResendingLink ? t('resending') : t('resend')}
        cancelLabel={t('cancel')}
        confirmDisabled={isResendingLink}
        destructive={false}
        autoFocusConfirm
      />

      {/* Manage permissions / actions modal */}
      {isEditModalOpen && selectedUser?.id && (
        <Modal
          isOpen={isEditModalOpen}
          onClose={() => setEditModalOpen(false)}
          size="small"
          caption={t('manage-user-permissions')}
          title={`${selectedUser.first_name} ${selectedUser.last_name}`}
          footerActions={[
            <Button
              onClick={() => setEditModalOpen(false)}
              label={t('cancel')}
              size="small"
              variant="smoke"
            />,
            <Button
              label={t('save')}
              onClick={() => {
                setEditModalOpen(false);
                handleUserAction(selectedUser.id);
              }}
              size="small"
              variant="primary"
            />
          ]}
        >
          <div className="w-full px-4 py-6">
            <div className="mx-auto w-full max-w-md">
              <RadioGroup value={selectedOption} onChange={setSelectedOption}>
                <Label className="sr-only">{t('option')}</Label>
                <div className="space-y-6">
                  {statusType.map(option => (
                    <Radio
                      key={option.id}
                      value={option}
                      className={({ focus, checked }) =>
                        classNames(
                          'relative flex cursor-pointer rounded-2xl px-3 py-2 shadow-sm border w-full',
                          checked
                            ? 'border-gray-300 dark:border-zinc-600'
                            : 'border-gray-200 dark:border-zinc-700',
                          focus ? 'ring-2 ring-offset-2 ring-primary' : ''
                        )
                      }
                    >
                      {({ checked }) => (
                        <div className="flex w-full items-center justify-between">
                          <div className="text-sm">
                            <Label
                              as="p"
                              className={classNames(
                                'font-normal',
                                checked
                                  ? 'text-primary'
                                  : 'text-gray-400 dark:text-zinc-500'
                              )}
                            >
                              {option.title}
                            </Label>
                          </div>
                          {checked && (
                            <Check className="h-4 w-4 text-primary" />
                          )}
                        </div>
                      )}
                    </Radio>
                  ))}
                </div>
              </RadioGroup>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};
